import React, { useId, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';

import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { QuickInsertPlugin } from '@atlaskit/editor-plugin-quick-insert';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/new';
import { CloseButton, ModalBody, ModalFooter, ModalHeader } from '@atlaskit/modal-dialog';
import { Stack, Text } from '@atlaskit/primitives/compiled';

import type { DialogsContainerProps } from '@confluence/dialogs/entry-points/DialogsStateContainer.ts';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { withFlags, type WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';

import {
	ANALYTICS_ACTION,
	ANALYTICS_SOURCE,
	ANALYTICS_ORIGIN,
	ANALYTICS_SUBJECT,
	ANALYTICS_SUBJECT_ID,
} from '../constants/snippetAnalytics.ts';
import { useSnippetsActions } from '../store/snippetsStore.ts';
import type { Snippet } from '../snippetTypes.ts';

import { messages } from './DeleteSnippetModal.i18n.ts';

export enum DeleteSnippetModalOutcome {
	DELETED = 'deleted',
	CANCELLED = 'cancelled',
}

type DeleteSnippetModalProps = DialogsContainerProps &
	WithFlagsProps & {
		snippet: Snippet;
		editorAPI?: PublicPluginAPI<[QuickInsertPlugin]> | null;
	};

const DeleteSnippetModalWithFlags = ({
	snippet,
	onClose,
	onCloseComplete,
	flags,
	editorAPI,
}: DeleteSnippetModalProps) => {
	const { Modal } = useChoreographerAssets();
	const titleId = useId();
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const snippetsActions = useSnippetsActions();
	const [isDeleting, setIsDeleting] = useState(false);
	const [error, setError] = useState<Error | null>(null);

	const fireDeleteAnalytics = (outcome: DeleteSnippetModalOutcome) => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: ANALYTICS_SOURCE,
				actionSubject: ANALYTICS_SUBJECT.BUTTON,
				action: ANALYTICS_ACTION.CLOSED,
				actionSubjectId: ANALYTICS_SUBJECT_ID.DELETE_SNIPPET,
				type: 'ui',
				attributes: {
					origin: ANALYTICS_ORIGIN.DELETE_SNIPPET_MODAL,
					snippetId: snippet.snippetId,
					outcome,
				},
			},
		}).fire();
	};

	const handleCancel = () => {
		fireDeleteAnalytics(DeleteSnippetModalOutcome.CANCELLED);
		onClose();
	};

	const handleConfirm = async () => {
		setIsDeleting(true);
		setError(null);
		try {
			await snippetsActions.deleteSnippet(snippet.snippetId, {
				flags,
				intl,
				editorAPI: editorAPI ?? null,
			});
			fireDeleteAnalytics(DeleteSnippetModalOutcome.DELETED);
			onClose();
		} catch (err) {
			setError(err instanceof Error ? err : new Error(String(err)));
		} finally {
			setIsDeleting(false);
		}
	};

	return (
		<Modal onClose={handleCancel} onCloseComplete={onCloseComplete}>
			<ModalHeader>
				<Text id={titleId} weight="semibold" size="large">
					<FormattedMessage {...messages.deleteTemplateModalTitle} />
				</Text>
				<CloseButton onClick={handleCancel} />
			</ModalHeader>
			<ModalBody>
				<Stack space="space.200">
					<Text>
						<FormattedMessage {...messages.deleteTemplateModalBody} />
					</Text>
					{error && (
						<Text color="color.text.danger" size="small">
							{error.message}
						</Text>
					)}
				</Stack>
			</ModalBody>
			<ModalFooter>
				<ButtonGroup>
					<Button appearance="subtle" onClick={handleCancel} isDisabled={isDeleting}>
						<FormattedMessage {...messages.deleteTemplateModalCancelButton} />
					</Button>
					<Button
						appearance="danger"
						onClick={handleConfirm}
						isLoading={isDeleting}
						isDisabled={isDeleting}
					>
						<FormattedMessage {...messages.deleteTemplateModalConfirmButton} />
					</Button>
				</ButtonGroup>
			</ModalFooter>
		</Modal>
	);
};

export const DeleteSnippetModal = withFlags(DeleteSnippetModalWithFlags);
